/**
 * buildFreshness — refuse to keep running code the server has replaced.
 *
 * WHY THIS EXISTS
 * ---------------
 * 2026-08-16. The operator reported, with screenshots, that the LEARNING chart
 * was rendering his own hand-drawn shapes. Every layer was verified correct:
 *
 *   server   `?non_human=true` -> 8 AI shapes    `?human_only=true` -> 49 human
 *   bundle   originFilter: s==="learning"?"AI":"HUMAN"      (present in dist)
 *   client   listShapes({origin}) -> non_human=true          (present in dist)
 *   ws feed  strict origin lock on every pushed shape        (present in dist)
 *
 * The bug was that NONE of that code was running. His tab had been open since
 * before the deploy, so it was still executing the previous bundle — which the
 * shape-engine access log proves, because the request it sent has a shape the
 * current bundle cannot produce:
 *
 *   his browser  05:36  GET /api/v2/shapes?human_only=true&limit=200
 *   this bundle         GET /api/v2/shapes?limit=1000&symbol=…&non_human=true
 *
 * Different parameter ORDER and a limit of 200 that no current call site emits.
 * A single-page app never reloads on its own, so a fix can be built, deployed
 * and verified — and still not be the code in front of the person reporting the
 * bug. That gap has cost this project entire debugging sessions more than once.
 *
 * WHAT THIS DOES
 * --------------
 * index.html is served `no-store` while the hashed assets are `immutable`, so
 * re-fetching the HTML is a cheap, always-fresh way to ask "which bundle is
 * current?". If the answer stops matching the bundle that is running, say so.
 *
 * It NEVER reloads by itself while the operator might be mid-drawing — losing an
 * unsaved shape to a surprise refresh would be a worse bug than the one this
 * prevents. It reports, and the decision stays with the human.
 */

/** The asset the running document was loaded with — read once, at boot. */
function runningBundle(): string | null {
  const scripts = Array.from(document.querySelectorAll('script[src]'));
  for (const s of scripts) {
    const src = (s as HTMLScriptElement).getAttribute('src') || '';
    const m = src.match(/index-[A-Za-z0-9_-]+\.js/);
    if (m) return m[0];
  }
  return null;
}

async function currentBundle(): Promise<string | null> {
  // cache: 'no-store' AND a cache-buster: some intermediaries honour neither
  // Cache-Control nor the fetch cache mode, and a stale HTML here would report
  // "up to date" forever — the exact failure this module exists to catch.
  const res = await fetch(`/?_bust=${Date.now()}`, {
    cache: 'no-store',
    signal: AbortSignal.timeout(8000),
  });
  if (!res.ok) return null;
  const html = await res.text();
  const m = html.match(/index-[A-Za-z0-9_-]+\.js/);
  return m ? m[0] : null;
}

export type FreshnessState = { running: string | null; current: string | null; stale: boolean };

let listeners: Array<(s: FreshnessState) => void> = [];
let state: FreshnessState = { running: null, current: null, stale: false };

export function onBuildStale(fn: (s: FreshnessState) => void): () => void {
  listeners.push(fn);
  if (state.stale) fn(state);
  return () => { listeners = listeners.filter(l => l !== fn); };
}

export function getFreshness(): FreshnessState {
  return state;
}

/** Start watching. Safe to call once, at boot. */
export function watchBuildFreshness(intervalMs = 120_000): void {
  const running = runningBundle();
  // In dev there is no hashed bundle to compare against — nothing to police.
  if (!running) return;
  state = { running, current: running, stale: false };

  const check = async () => {
    // Don't poll a backgrounded tab; it will be checked the moment it returns.
    if (document.visibilityState === 'hidden') return;
    try {
      const current = await currentBundle();
      if (!current) return;                       // network hiccup — say nothing
      const stale = current !== running;
      state = { running, current, stale };
      if (stale) listeners.forEach(fn => { try { fn(state); } catch { /* ignore */ } });
    } catch {
      /* offline or slow — a failed check is never evidence of staleness */
    }
  };

  // Check on return-to-tab: that is when someone comes back to a page they left
  // open overnight, which is precisely the case that went undiagnosed.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void check();
  });
  window.setInterval(() => { void check(); }, intervalMs);
  window.setTimeout(() => { void check(); }, 15_000);
}
